/**
 * Screen Composer layout, saved on the screen (`BOS-Screen.composer`) and played by the screen player.
 * Widget positions are fractions (0..1) of the screen so the layout survives a resolution or
 * orientation change. Font sizes are pixels at the screen's own resolution.
 * Keep the types and limits in step with st-ck-server `helpers/beamonscreen/screenComposer.ts`.
 *
 * Imported by the screen player bundle: no Redux, no API client, no portal-only modules here.
 */

export const SCREEN_WIDGET_TYPES = ['reel-player', 'weather', 'news-ticker', 'clock', 'stock-ticker', 'cricket-score', 'on-demand-player'] as const;
export type ScreenWidgetType = typeof SCREEN_WIDGET_TYPES[number];

export const COMPOSER_MAX_WIDGETS = 20;
export const MIN_WIDGET_FRACTION = 0.02;

export interface WidgetLook {
    fontFamily: string;
    fontSize: number;
    color: string;
    backgroundColor: string;
}

export interface ReelPlayerWidgetConfig {
    fit: 'contain' | 'cover';
    backgroundColor: string;
}

export type OnDemandQrPosition = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right' | 'center';

export interface OnDemandPlayerWidgetConfig {
    idleDisplay: 'qr' | 'none';
    /** Empty uses the screen's On Demand URL. */
    qrUrl: string;
    qrSize: number;
    qrPosition: OnDemandQrPosition;
    caption: string;
    backgroundColor: string;
    fit: 'contain' | 'cover';
    messageColor: string;
    messageFontSize: number;
    messageBackground: string;
    greeting: string;
    thankYou: string;
}

export interface WeatherWidgetConfig extends WidgetLook {
    /** `place (lat, lng)` like the screen location; empty = use the screen location. */
    locationOverride: string;
    labelOverride: string;
    showIcon: boolean;
}

export interface NewsTickerWidgetConfig extends WidgetLook {
    feedUrl: string;
    fields: string[];
    fieldJoiner: string;
    separator: string;
    speedPxPerSec: number;
    maxItems: number;
}

export const SERVER_REFRESH_OPTIONS = [30, 60, 120, 300, 900] as const;
export const TICKER_REFRESH_OPTIONS = [15, 30, 60, 120, 300] as const;
export type StockLabelField = 'symbol' | 'name' | 'both';

export interface StockTickerWidgetConfig extends WidgetLook {
    separator: string;
    speedPxPerSec: number;
    symbols: string[];
    labelField: StockLabelField;
    showChange: boolean;
    showChangePercent: boolean;
    showTrend: boolean;
    showCurrency: boolean;
    upColor: string;
    downColor: string;
    /** How often st-ck-server asks Yahoo. Shared by every screen. Minimum 30 seconds. */
    serverRefreshSec: number;
    /** How often this ticker asks the server. */
    tickerRefreshSec: number;
}

export interface StockQuote {
    symbol: string;
    name: string;
    price: number;
    change: number;
    changePct: number;
    currency: string;
    marketState: string;
    delayedMin: number;
    fetchedAt: string;
}

export interface StockQuotesPayload {
    quotes: StockQuote[];
    missing: string[];
    fetchedAt: string;
    stale: boolean;
}

export type CricketStyle = 'compact' | 'broadcast' | 'ball-by-ball' | 'scorecard' | 'ticker';
export const CRICKET_STYLES: CricketStyle[] = ['compact', 'broadcast', 'ball-by-ball', 'scorecard', 'ticker'];
export const CRICKET_DISPLAY_REFRESH = [5, 10, 15, 30] as const;

export interface CricketTeamSide {
    name: string;
    short: string;
    img: string;
    runs: number | null;
    wickets: number | null;
    overs: string;
}
export interface CricketMatchSnapshot {
    matchId: string;
    name: string;
    series: string;
    matchType: string;
    status: string;
    state: 'fixture' | 'live' | 'paused' | 'result';
    startsAt: string;
    teams: CricketTeamSide[];
    batting: Array<{ name: string; runs: number; balls: number }>;
    bowling: { name: string; overs: string; runs: number; wickets: number } | null;
    recentBalls: Array<{ text: string; runs: number; wicket: boolean; extra: string }>;
    crr: string;
    detail: 'score' | 'scorecard' | 'bbb';
    fetchedAt: string;
}
export interface CricketWorkerConfig {
    teams: string[];
    discoverySec: number;
    liveSec: number;
    detailSec: number;
    pausedSec: number;
    ttlSec: number;
}
export interface CricketWidgetPayload {
    matches: CricketMatchSnapshot[];
    next: CricketMatchSnapshot | null;
    workerOnline: boolean;
    usage: { hitsToday: number; hitsLimit: number } | null;
    config: CricketWorkerConfig | null;
    expiresAt: string | null;
}
export interface CricketScoreWidgetConfig extends WidgetLook {
    teams: string[];
    matchTypes: Array<'test' | 'odi' | 't20'>;
    style: CricketStyle;
    showNextMatch: boolean;
    rotateSec: number;
    displayRefreshSec: number;
    accentColor: string;
    showFlags: boolean;
    /** IANA zone for the next-match stamp. Empty uses the screen timezone. */
    timezoneOverride: string;
}

export interface ClockWidgetConfig extends WidgetLook {
    format: ClockFormat;
    /** IANA zone; empty = the screen's own timezone (set from its location). */
    timezoneOverride: string;
    /** Adds a day-first date under time-only formats. */
    showDate?: boolean;
}

interface WidgetBase<T extends ScreenWidgetType, C> {
    id: string;
    type: T;
    name: string;
    visible: boolean;
    locked: boolean;
    x: number;
    y: number;
    width: number;
    height: number;
    config: C;
}

export type ReelPlayerWidget = WidgetBase<'reel-player', ReelPlayerWidgetConfig>;
export type WeatherWidget = WidgetBase<'weather', WeatherWidgetConfig>;
export type NewsTickerWidget = WidgetBase<'news-ticker', NewsTickerWidgetConfig>;
export type StockTickerWidget = WidgetBase<'stock-ticker', StockTickerWidgetConfig>;
export type CricketScoreWidget = WidgetBase<'cricket-score', CricketScoreWidgetConfig>;
export type ClockWidget = WidgetBase<'clock', ClockWidgetConfig>;
export type OnDemandPlayerWidget = WidgetBase<'on-demand-player', OnDemandPlayerWidgetConfig>;
export type ScreenWidget = ReelPlayerWidget | WeatherWidget | NewsTickerWidget | StockTickerWidget | CricketScoreWidget | ClockWidget | OnDemandPlayerWidget;

export interface ScreenComposerLayout {
    revision: number;
    updatedAt?: string | null;
    updatedBy?: string;
    widgets: ScreenWidget[];
}

/** Live data the device fetches from `/bos/device/widgets` and serves as `/widgets/widgets.json`. */
export interface WeatherNow {
    location: string;
    tempC: number;
    icon: string;
    label: string;
    isDay?: boolean;
    observedAt?: string;
}

export interface NewsNow {
    title?: string;
    items: string[];
    error?: string;
    fetchedAt?: string;
}

export interface WidgetData {
    revision?: number;
    fetchedAt?: string;
    weather: Record<string, WeatherNow>;
    news: Record<string, NewsNow>;
}

export const EMPTY_WIDGET_DATA: WidgetData = { weather: {}, news: {} };

// ---------------------------------------------------------------------------
// Fonts and clock formats
// ---------------------------------------------------------------------------

/** Same list as Reel Maker `FONT_FAMILIES` (and the server allow-list). */
export const COMPOSER_FONT_FAMILIES: ReadonlyArray<{ label: string; value: string }> = [
    { label: 'Inter / System', value: 'Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' },
    { label: 'Arial', value: 'Arial, Helvetica, sans-serif' },
    { label: 'Helvetica Neue', value: '"Helvetica Neue", Helvetica, Arial, sans-serif' },
    { label: 'Verdana', value: 'Verdana, Geneva, sans-serif' },
    { label: 'Trebuchet MS', value: '"Trebuchet MS", Helvetica, sans-serif' },
    { label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
    { label: 'Times New Roman', value: '"Times New Roman", Times, serif' },
    { label: 'Palatino', value: '"Palatino Linotype", "Book Antiqua", Palatino, serif' },
    { label: 'Garamond', value: 'Garamond, "EB Garamond", serif' },
    { label: 'Impact', value: 'Impact, Haettenschweiler, "Arial Narrow Bold", sans-serif' },
    { label: 'Courier New', value: '"Courier New", Courier, monospace' },
    { label: 'Lucida Console', value: '"Lucida Console", Monaco, monospace' },
    { label: 'Comic Sans MS', value: '"Comic Sans MS", "Comic Sans", cursive' },
    { label: 'Brush Script', value: '"Brush Script MT", "Brush Script Std", cursive' },
    { label: 'Copperplate', value: 'Copperplate, "Copperplate Gothic Light", fantasy' },
];

export const CLOCK_FORMATS = [
    'HH:mm',
    'HH:mm:ss',
    'h:mm a',
    'h:mm:ss a',
    'ddd D MMM YYYY HH:mm',
    'dddd, D MMMM YYYY',
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    'YYYY-MM-DD',
    'stacked',
] as const;
export type ClockFormat = typeof CLOCK_FORMATS[number];

export const CLOCK_FORMAT_LABELS: Record<ClockFormat, string> = {
    'HH:mm': 'Time, 24 hour (14:05)',
    'HH:mm:ss': 'Time with seconds, 24 hour (14:05:09)',
    'h:mm a': 'Time, 12 hour (2:05 PM)',
    'h:mm:ss a': 'Time with seconds, 12 hour (2:05:09 PM)',
    'ddd D MMM YYYY HH:mm': 'Date and time (Tue 27 Oct 2026 14:05)',
    'dddd, D MMMM YYYY': 'Long date (Tuesday, 27 October 2026)',
    'DD/MM/YYYY': 'Date, day first (27/10/2026)',
    'MM/DD/YYYY': 'Date, month first (10/27/2026)',
    'YYYY-MM-DD': 'Date, ISO (2026-10-27)',
    stacked: 'Time above date (14:05 / Tuesday 27 October)',
};

const partsOf = (date: Date, timeZone: string | undefined, options: Intl.DateTimeFormatOptions) => {
    const zone = timeZone || undefined;
    let parts: Intl.DateTimeFormatPart[];
    try {
        parts = new Intl.DateTimeFormat('en-GB', { ...options, timeZone: zone }).formatToParts(date);
    } catch {
        parts = new Intl.DateTimeFormat('en-GB', options).formatToParts(date);
    }
    const map: Record<string, string> = {};
    for (const p of parts) if (p.type !== 'literal') map[p.type] = p.value;
    return map;
};

/** Clock text for a preset, one string per line. Locale-independent so screens match the portal preview. */
export const formatClock = (date: Date, format: ClockFormat, timeZone?: string): string[] => {
    const d = partsOf(date, timeZone, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
    });
    const names = partsOf(date, timeZone, { weekday: 'long', month: 'long', day: 'numeric' });
    const shortNames = partsOf(date, timeZone, { weekday: 'short', month: 'short' });
    const hour24 = Number(d.hour) % 24;
    const hour12 = hour24 % 12 || 12;
    const ampm = hour24 < 12 ? 'AM' : 'PM';
    const hhmm = `${String(hour24).padStart(2, '0')}:${d.minute}`;
    switch (format) {
        case 'HH:mm':
            return [hhmm];
        case 'HH:mm:ss':
            return [`${hhmm}:${d.second}`];
        case 'h:mm a':
            return [`${hour12}:${d.minute} ${ampm}`];
        case 'h:mm:ss a':
            return [`${hour12}:${d.minute}:${d.second} ${ampm}`];
        case 'ddd D MMM YYYY HH:mm':
            return [`${shortNames.weekday} ${Number(d.day)} ${shortNames.month} ${d.year} ${hhmm}`];
        case 'dddd, D MMMM YYYY':
            return [`${names.weekday}, ${Number(d.day)} ${names.month} ${d.year}`];
        case 'DD/MM/YYYY':
            return [`${d.day}/${d.month}/${d.year}`];
        case 'MM/DD/YYYY':
            return [`${d.month}/${d.day}/${d.year}`];
        case 'YYYY-MM-DD':
            return [`${d.year}-${d.month}-${d.day}`];
        case 'stacked':
        default:
            return [hhmm, `${names.weekday} ${Number(d.day)} ${names.month}`];
    }
};

const CLOCK_FORMAT_HAS_DATE = new Set<ClockFormat>([
    'ddd D MMM YYYY HH:mm',
    'dddd, D MMMM YYYY',
    'DD/MM/YYYY',
    'MM/DD/YYYY',
    'YYYY-MM-DD',
    'stacked',
]);

/** Day-first date in the given zone: 30/10/2026. */
export const formatDateStamp = (date: Date, timeZone?: string): string => {
    const d = partsOf(date, timeZone, { year: 'numeric', month: '2-digit', day: '2-digit' });
    return `${d.day}/${d.month}/${d.year}`;
};

/** Next-match stamp in the given zone: 30/10/2026 18:30. Values without a zone are UTC. */
export const formatFixtureStamp = (raw: string, timeZone?: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) return '';
    const iso = /(?:[zZ]|[+-]\d{2}:?\d{2})$/.test(trimmed) ? trimmed : `${trimmed.replace(' ', 'T')}Z`;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return trimmed;
    const d = partsOf(date, timeZone, { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
    return `${d.day}/${d.month}/${d.year} ${d.hour}:${d.minute}`;
};

/** Time lines, plus a date line when the chosen format does not already include one. */
export const clockLines = (date: Date, format: ClockFormat, timeZone: string | undefined, showDate: boolean): string[] => {
    const lines = formatClock(date, format, timeZone);
    if (!showDate || CLOCK_FORMAT_HAS_DATE.has(format)) return lines;
    return [...lines, formatDateStamp(date, timeZone)];
};

// ---------------------------------------------------------------------------
// Defaults and helpers
// ---------------------------------------------------------------------------

export const WIDGET_LABELS: Record<ScreenWidgetType, string> = {
    'reel-player': 'Reel Player',
    weather: 'Weather',
    'news-ticker': 'News Ticker',
    'stock-ticker': 'Stock Ticker',
    'cricket-score': 'Cricket Score',
    clock: 'Time',
    'on-demand-player': 'On Demand Player',
};

const newWidgetId = () =>
    typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID().replace(/-/g, '').slice(0, 20)
        : `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;

const DEFAULT_FONT = COMPOSER_FONT_FAMILIES[0].value;

export const createWidget = (type: ScreenWidgetType, existing: ScreenWidget[]): ScreenWidget => {
    const count = existing.filter((w) => w.type === type).length;
    const name = count ? `${WIDGET_LABELS[type]} ${count + 1}` : WIDGET_LABELS[type];
    const base = { id: newWidgetId(), name, visible: true, locked: false };
    switch (type) {
        case 'reel-player':
            return { ...base, type, x: 0, y: 0, width: 0.75, height: 0.85, config: { fit: 'contain', backgroundColor: '#000000' } };
        case 'weather':
            return {
                ...base,
                type,
                x: 0.77,
                y: 0.02,
                width: 0.21,
                height: 0.16,
                config: { fontFamily: DEFAULT_FONT, fontSize: 48, color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.45)', locationOverride: '', labelOverride: '', showIcon: true },
            };
        case 'news-ticker':
            return {
                ...base,
                type,
                x: 0,
                y: 0.9,
                width: 1,
                height: 0.08,
                config: {
                    fontFamily: DEFAULT_FONT,
                    fontSize: 40,
                    color: '#ffffff',
                    backgroundColor: '#b71c1c',
                    feedUrl: '',
                    fields: ['title'],
                    fieldJoiner: ' - ',
                    separator: '  •  ',
                    speedPxPerSec: 120,
                    maxItems: 15,
                },
            };
        case 'stock-ticker':
            return {
                ...base,
                type,
                x: 0,
                y: 0.82,
                width: 1,
                height: 0.08,
                config: {
                    fontFamily: DEFAULT_FONT,
                    fontSize: 36,
                    color: '#ffffff',
                    backgroundColor: '#0d47a1',
                    separator: '   •   ',
                    speedPxPerSec: 90,
                    symbols: ['^FTSE', '^GSPC', 'AAPL', 'MSFT', 'GBPUSD=X'],
                    labelField: 'both',
                    showChange: true,
                    showChangePercent: true,
                    showTrend: true,
                    showCurrency: true,
                    upColor: '#69f0ae',
                    downColor: '#ff8a80',
                    serverRefreshSec: 30,
                    tickerRefreshSec: 30,
                },
            };
        case 'cricket-score':
            return {
                ...base,
                type,
                x: 0.02,
                y: 0.72,
                width: 0.7,
                height: 0.12,
                config: {
                    fontFamily: DEFAULT_FONT,
                    fontSize: 28,
                    color: '#ffffff',
                    backgroundColor: '#0b3d2e',
                    teams: ['India'],
                    matchTypes: [],
                    style: 'compact',
                    showNextMatch: true,
                    rotateSec: 15,
                    displayRefreshSec: 10,
                    accentColor: '#69f0ae',
                    showFlags: true,
                    timezoneOverride: '',
                },
            };
        case 'on-demand-player':
            return {
                ...base,
                type,
                x: 0.05,
                y: 0.08,
                width: 0.42,
                height: 0.62,
                config: {
                    idleDisplay: 'qr',
                    qrUrl: '',
                    qrSize: 0.7,
                    qrPosition: 'center',
                    caption: 'Scan to play on this screen',
                    backgroundColor: '#000000',
                    fit: 'contain',
                    messageColor: '#ffffff',
                    messageFontSize: 28,
                    messageBackground: 'rgba(0,0,0,0.72)',
                    greeting: 'Now playing',
                    thankYou: 'Thank you',
                },
            };
        case 'clock':
        default:
            return {
                ...base,
                type: 'clock',
                x: 0.77,
                y: 0.2,
                width: 0.21,
                height: 0.12,
                config: { fontFamily: DEFAULT_FONT, fontSize: 64, color: '#ffffff', backgroundColor: 'rgba(0,0,0,0.45)', format: 'HH:mm', timezoneOverride: '', showDate: false },
            };
    }
};

/** Keeps a widget inside the screen and at least the minimum size; tickers always span the width. */
export const clampWidget = <W extends ScreenWidget>(w: W): W => {
    let width = Math.min(1, Math.max(MIN_WIDGET_FRACTION, w.width));
    const height = Math.min(1, Math.max(MIN_WIDGET_FRACTION, w.height));
    let x = Math.min(1 - width, Math.max(0, w.x));
    const y = Math.min(1 - height, Math.max(0, w.y));
    if (w.type === 'news-ticker' || w.type === 'stock-ticker') {
        x = 0;
        width = 1;
    }
    return { ...w, x, y, width, height };
};

/** Every widget keeps the place and layer the user set, including while an On Demand playlist plays. */
export const onDemandTakeover = (widgets: ScreenWidget[], _active: boolean): ScreenWidget[] => widgets;

export interface OnDemandProgress {
    bytesDone: number;
    bytesTotal: number;
    filesDone: number;
    filesTotal: number;
}

export type OnDemandDownloadState = 'waiting' | 'downloading' | 'ready' | 'error';

export interface OnDemandQueueEntry {
    id: string;
    name: string;
    userFirstName: string;
    state: OnDemandDownloadState;
    progress?: OnDemandProgress;
}

export interface OnDemandLocalItem {
    type: 'video' | 'audio' | 'reel';
    key: string;
    name: string;
    durationMs: number;
    url?: string;
    mimeType?: string;
    reelId?: string;
    version?: number;
    width?: number;
    height?: number;
    canvas?: unknown;
}

export interface OnDemandLocalPlaylist extends OnDemandQueueEntry {
    kind: string;
    maxPlays: number;
    playCount: number;
    totalDurationMs: number;
    items: OnDemandLocalItem[];
}

/** Written by the screen agent to `/widgets/ondemand.json`. */
export interface OnDemandStateFile {
    enabled: boolean;
    qrUrl: string;
    queueCount: number;
    playlists: OnDemandLocalPlaylist[];
}

/** Bytes finished, or files finished when the sizes are not known yet. */
export const onDemandPercent = (progress?: OnDemandProgress): number => {
    if (!progress) return 0;
    if (progress.bytesTotal > 0) return Math.min(100, Math.round((progress.bytesDone / progress.bytesTotal) * 100));
    if (progress.filesTotal > 0) return Math.min(100, Math.round((progress.filesDone / progress.filesTotal) * 100));
    return 0;
};

export const onDemandStatusText = (state: OnDemandDownloadState, progress?: OnDemandProgress): string => {
    if (state === 'ready') return 'Ready';
    if (state === 'downloading') return `Downloading ${onDemandPercent(progress)}%`;
    if (state === 'error') return 'Download failed';
    return 'Waiting';
};

/** "3 in queue · Next: Lobby promo by Sharad · Downloading 42%" */
export const onDemandStripLabel = (queueCount: number, next: OnDemandQueueEntry | null): string => {
    if (queueCount < 1) return '';
    if (!next) return `${queueCount} in queue`;
    const who = next.userFirstName ? ` by ${next.userFirstName}` : '';
    return `${queueCount} in queue · Next: ${next.name}${who} · ${onDemandStatusText(next.state, next.progress)}`;
};

/** "Downloading next: Lobby promo by Sharad, 42%" */
export const onDemandBannerLabel = (entry: OnDemandQueueEntry): string => {
    const who = entry.userFirstName ? ` by ${entry.userFirstName}` : '';
    if (entry.state === 'ready') return `Ready to play: ${entry.name}${who}`;
    if (entry.state === 'error') return `Could not download: ${entry.name}${who}`;
    return `Downloading next: ${entry.name}${who}, ${onDemandPercent(entry.progress)}%`;
};

/** `1920x1080` (or `1920 × 1080`) to numbers; swapped for portrait orientations. */
export const screenDesignSize = (resolution: string | undefined, orientation: string | undefined): { width: number; height: number } => {
    const m = (resolution || '').match(/(\d{3,5})\s*[x×]\s*(\d{3,5})/i);
    let width = m ? Number(m[1]) : 1920;
    let height = m ? Number(m[2]) : 1080;
    if (orientation === 'portrait' || orientation === 'portrait-flipped') [width, height] = [height, width];
    return { width, height };
};
